import { gql, useQuery } from '@apollo/client';
import { getCountryAbbreviationFromCustomsBrokerId } from '@xbcb/core';
import {
  DateSearchOperator,
  KeywordSearchOperator,
  UsCbpEntryReleaseStatus,
  UsCbpEntrySummaryStatus,
} from '__generated__/graphql';
import { useCurrentUser } from 'libs/hooks';
import { usePageVisibility } from 'react-page-visibility';
import { UiStage } from '@xbcb/ui-types';
import { AccountType } from '@xbcb/shared-types';
import { getEnv } from '@xbcb/ui-env';
import { useTypedSelector } from 'reducers';

const entryCreatedCutoffTime = '2020-01-01';

const combinedEntryQuery = gql`
  query combinedEntryFiledData(
    $usEntryInput: SearchUsConsumptionEntryInput!
    $usType86Input: SearchUsType86EntryInput!
  ) {
    searchUsConsumptionEntries(input: $usEntryInput) {
      totalResults
    }
    searchUsType86Entries(input: $usType86Input) {
      totalResults
    }
  }
`;

export interface UseEntryStatisticsReturn {
  totalEntriesFiled: number;
  isFeatureEnabled: boolean;
}

export const useEntryStatistics = (): UseEntryStatisticsReturn => {
  const isVisible = usePageVisibility();
  const currentUser = useCurrentUser();

  const stage = getEnv().stage;

  const currentActiveBrokerId = useTypedSelector(
    (state) => state.activeBroker.currentActiveBroker,
  );

  // fallback in case the broker is not found in local storage
  const getCountryFromLocale = () => {
    const locale = navigator.language || navigator.languages[0];
    // This gives you language-country code like "en-US"
    const countryCode = locale.split('-')[1];
    return countryCode; // Returns "US", "GB", etc.
  };

  const activeCountry = currentActiveBrokerId
    ? getCountryAbbreviationFromCustomsBrokerId(currentActiveBrokerId)
    : getCountryFromLocale();

  const isOperatorUser = currentUser?.accountType === AccountType.OPERATOR;

  /* only needed to show in US for operator users */
  const isFeatureEnabled = activeCountry === 'US' && isOperatorUser;

  // Reduce network calls for geography where feature is not enabled
  // or the card is not launched
  const skipCombinedQuery =
    !currentUser?.id || !isVisible || !isFeatureEnabled || !isOperatorUser;

  const { data } = useQuery(combinedEntryQuery, {
    skip: skipCombinedQuery,
    variables: {
      usEntryInput: {
        searchCriteria: {
          entryNumber: { operator: KeywordSearchOperator.EXISTS },
          summaryStatus: {
            values: [UsCbpEntrySummaryStatus.FILED],
            operator: KeywordSearchOperator.ONE_OF,
          },
          createdTime: {
            value: entryCreatedCutoffTime,
            operator: DateSearchOperator.AFTER,
          },
          deletedTime: { operator: DateSearchOperator.DOES_NOT_EXIST },
        },
      },
      usType86Input: {
        searchCriteria: {
          entryNumber: { operator: KeywordSearchOperator.EXISTS },
          releaseStatus: {
            values: [UsCbpEntryReleaseStatus.RELEASED],
            operator: KeywordSearchOperator.ONE_OF,
          },
          createdTime: {
            value: entryCreatedCutoffTime,
            operator: DateSearchOperator.AFTER,
          },
          deletedTime: { operator: DateSearchOperator.DOES_NOT_EXIST },
        },
      },
    },
    pollInterval: 60000,
  });

  const usEntryFiled = data?.searchUsConsumptionEntries?.totalResults ?? 0;
  const type86EntryFiled = data?.searchUsType86Entries?.totalResults ?? 0;

  // There is a diff in value due to indexer issue where 68114 entries
  // (09/2020 - 06/2021) are not indexed on broker id.
  // WOA adds broker id on backend causing UI to reflect different filed entries.
  // Discussed with Ankit to add the diff manually in prod instead of reindexing
  const delta = stage === UiStage.PROD ? 68114 : 0;
  const totalEntriesFiled = usEntryFiled + type86EntryFiled + delta;

  return {
    totalEntriesFiled,
    isFeatureEnabled,
  };
};
